import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

type ConfirmVariant = 'destructive' | 'primary' | 'info';

type ConfirmModalProps = {
  open: boolean;
  title: string;
  description?: string;
  preview?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  hideCancel?: boolean;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const VARIANT_STYLES: Record<ConfirmVariant, {
  iconBg: string;
  iconColor: string;
  icon: typeof AlertTriangle;
  confirmBtn: string;
}> = {
  destructive: {
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-600',
    icon: AlertTriangle,
    confirmBtn: 'bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/20',
  },
  primary: {
    iconBg: 'bg-[#2f7d72]/10',
    iconColor: 'text-[#2f7d72]',
    icon: CheckCircle2,
    confirmBtn: 'bg-[#2f7d72] hover:bg-[#27685f] shadow-lg shadow-[#2f7d72]/20',
  },
  info: {
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    icon: Info,
    confirmBtn: 'bg-[#2f7d72] hover:bg-[#27685f] shadow-lg shadow-[#2f7d72]/20',
  },
};

const ConfirmModal = ({
  open,
  title,
  description,
  preview,
  confirmLabel,
  cancelLabel = 'Cancelar',
  variant = 'destructive',
  hideCancel = false,
  saving = false,
  onCancel,
  onConfirm,
}: ConfirmModalProps) => {
  if (!open) return null;

  const styles = VARIANT_STYLES[variant];
  const Icon = styles.icon;
  const defaultConfirmLabel =
    variant === 'destructive' ? 'Excluir' : variant === 'info' ? 'Entendi' : 'Confirmar';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.iconBg} ${styles.iconColor}`}>
              <Icon size={20} />
            </span>
            <div>
              <h3 className="text-base font-bold text-slate-900">{title}</h3>
              {description && (
                <p className="mt-1 text-sm leading-5 text-slate-600">{description}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Fechar modal"
          >
            <X size={18} />
          </button>
        </div>

        {preview && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            {preview}
          </div>
        )}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {!hideCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${styles.confirmBtn}`}
          >
            {saving ? 'Processando...' : confirmLabel || defaultConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
